import React from 'react';
import { useQuery } from 'react-query';
import {
  X, TrendingUp, TrendingDown, Minus, ShieldAlert, Heart, Clock,
  Mail, MessageCircle, Phone, Calendar, FileText, CheckCircle2,
  AlertTriangle, CircleDot, ArrowDownLeft, ArrowUpRight, Wallet, Quote
} from 'lucide-react';
import { contextAPI } from '../services/api';

/* ---------- helpers ---------- */

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('pl-PL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-');
const isOverdue = (d) => d && new Date(d).getTime() < Date.now();

const sentimentColor = (s) => (s >= 7 ? 'bg-emerald-500' : s >= 4 ? 'bg-amber-500' : 'bg-rose-500');
const riskColor = (s) => (s >= 7 ? 'bg-rose-500' : s >= 4 ? 'bg-amber-500' : 'bg-emerald-500');

const TrendIcon = ({ trend }) => {
  if (trend === 'up') return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (trend === 'down') return <TrendingDown className="w-4 h-4 text-rose-600" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
};

const channelIcon = (ch) => {
  const map = {
    email: Mail, whatsapp: MessageCircle, phone: Phone,
    meeting: Calendar, task: CheckCircle2, internal: FileText
  };
  const Icon = map[ch] || CircleDot;
  return <Icon className="w-4 h-4" />;
};

const COMMIT_STATUS = {
  awaiting: { label: 'czekamy', cls: 'bg-amber-100 text-amber-800' },
  reminded: { label: 'przypomniane', cls: 'bg-blue-100 text-blue-800' },
  received_unconfirmed: { label: 'wpłynęło (niepotw.)', cls: 'bg-indigo-100 text-indigo-800' },
  confirmed: { label: 'potwierdzone', cls: 'bg-emerald-100 text-emerald-800' },
  overdue: { label: 'po terminie', cls: 'bg-rose-100 text-rose-800' },
  waived: { label: 'odpuszczone', cls: 'bg-gray-100 text-gray-600' }
};

/* ---------- small UI atoms ---------- */

const Gauge = ({ label, value, colorFn, icon }) => (
  <div className="flex-1 min-w-[120px]">
    <div className="flex items-center justify-between mb-1">
      <span className="flex items-center gap-1 text-xs font-medium text-gray-500">{icon}{label}</span>
      <span className="text-sm font-semibold text-gray-900">{value ?? 0}/10</span>
    </div>
    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
      <div className={`h-full rounded-full ${colorFn(value || 0)}`} style={{ width: `${(value || 0) * 10}%` }} />
    </div>
  </div>
);

const Chip = ({ children, tone = 'gray' }) => {
  const tones = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200'
  };
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${tones[tone]}`}>{children}</span>;
};

const Section = ({ title, icon, count, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {count !== undefined && <span className="text-xs text-gray-400">({count})</span>}
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => <p className="text-xs text-gray-400 italic">{text}</p>;

/* ---------- main panel ---------- */

export default function ContextCenter({ projectId, projectName, onClose }) {
  const { data, isLoading, isError } = useQuery(
    ['context', projectId],
    () => contextAPI.get(projectId),
    { enabled: !!projectId, refetchOnWindowFocus: false }
  );

  const ctx = data?.context;
  const events = data?.events || [];
  const st = ctx?.state || {};
  const commitments = ctx?.commitments || [];
  const openCommitments = commitments.filter((c) => !['confirmed', 'waived'].includes(c.status));

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-3xl h-full bg-gray-50 shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-indigo-600 text-white text-xs font-bold">CK</span>
              <h2 className="text-lg font-bold text-gray-900">Centrum Kontekstu</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{projectName}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {isLoading && <p className="text-sm text-gray-500">Ładowanie kontekstu…</p>}
          {isError && <p className="text-sm text-rose-600">Nie udało się pobrać kontekstu.</p>}

          {!isLoading && !ctx && (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center">
              <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-600 font-medium">Brak kontekstu dla tego projektu</p>
              <p className="text-xs text-gray-400 mt-1">
                Kontekst buduje Claude w Claude Code (rytuał INGEST → RECONCILE → COMMIT). Tu pojawi się obiektywny stan projektu.
              </p>
            </div>
          )}

          {ctx && (
            <>
              {/* pulse: sentiment + risk + relationship */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex items-center gap-1 text-sm font-semibold text-gray-900">Puls relacji</span>
                  <TrendIcon trend={st.sentiment?.trend} />
                  {st.phase && <Chip tone="blue">{st.phase}</Chip>}
                  <span className="ml-auto text-[11px] text-gray-400">
                    rev {ctx.revision} · {fmtDateTime(st.updatedAt || ctx.updatedAt)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Gauge label="Sentyment" value={st.sentiment?.score} colorFn={sentimentColor} icon={<Heart className="w-3.5 h-3.5" />} />
                  <Gauge label="Relacja" value={st.relationshipStrength} colorFn={sentimentColor} icon={<Heart className="w-3.5 h-3.5" />} />
                  <Gauge label="Churn" value={st.risk?.churn} colorFn={riskColor} icon={<ShieldAlert className="w-3.5 h-3.5" />} />
                  <Gauge label="Płatność" value={st.risk?.payment} colorFn={riskColor} icon={<Wallet className="w-3.5 h-3.5" />} />
                  <Gauge label="Scope-creep" value={st.risk?.scopeCreep} colorFn={riskColor} icon={<AlertTriangle className="w-3.5 h-3.5" />} />
                </div>
                {st.sentiment?.note && <p className="text-xs text-gray-600 mt-3">{st.sentiment.note}</p>}
                {st.summary && <p className="text-sm text-gray-700 mt-3 leading-relaxed">{st.summary}</p>}
                {(st.risk?.top || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {st.risk.top.map((r, i) => <Chip key={i} tone="rose">{r}</Chip>)}
                  </div>
                )}
              </div>

              {/* open loops */}
              <Section title="Otwarte pętle" icon={<Clock className="w-4 h-4 text-amber-600" />} count={openCommitments.length}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-[11px] font-semibold text-rose-600 uppercase tracking-wide mb-1">Na nas</p>
                    {(st.awaitingUs || []).length ? (st.awaitingUs.map((x, i) => (
                      <div key={i} className="text-xs text-gray-700 flex gap-1.5 mb-1"><ArrowUpRight className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" />{x}</div>
                    ))) : <Empty text="nic" />}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-amber-600 uppercase tracking-wide mb-1">Na nich</p>
                    {(st.awaitingThem || []).length ? (st.awaitingThem.map((x, i) => (
                      <div key={i} className="text-xs text-gray-700 flex gap-1.5 mb-1"><ArrowDownLeft className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />{x}</div>
                    ))) : <Empty text="nic" />}
                  </div>
                </div>
              </Section>

              {/* commitments ledger */}
              <Section title="Commitments / deadliny" icon={<CheckCircle2 className="w-4 h-4 text-indigo-600" />} count={commitments.length}>
                {commitments.length ? (
                  <div className="space-y-2">
                    {commitments.map((c) => {
                      const stt = COMMIT_STATUS[c.status] || COMMIT_STATUS.awaiting;
                      const overdue = isOverdue(c.due) && !['confirmed', 'waived'].includes(c.status);
                      return (
                        <div key={c._id} className={`flex items-start gap-3 p-2.5 rounded-lg border ${overdue ? 'border-rose-200 bg-rose-50' : 'border-gray-100 bg-gray-50'}`}>
                          <span className={`mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${c.owner === 'us' ? 'bg-rose-200 text-rose-800' : 'bg-amber-200 text-amber-800'}`}>
                            {c.owner === 'us' ? 'MY' : 'KLIENT'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800">{c.what}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${stt.cls}`}>{stt.label}</span>
                              <span className={`text-[11px] flex items-center gap-1 ${overdue ? 'text-rose-600 font-medium' : 'text-gray-500'}`}>
                                <Clock className="w-3 h-3" />{fmtDate(c.due)}
                              </span>
                            </div>
                            {c.reminderNote && <p className="text-[11px] text-gray-500 mt-1 italic">„{c.reminderNote}”</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : <Empty text="brak commitmentów" />}
              </Section>

              {/* commercial / scope */}
              <Section title="Zakres i pieniądze" icon={<Wallet className="w-4 h-4 text-emerald-600" />}>
                <div className="space-y-3">
                  {(ctx.commercial?.contractValue || ctx.commercial?.paymentStatus) && (
                    <div className="flex items-center gap-3 text-sm">
                      {ctx.commercial?.contractValue != null && (
                        <span className="font-semibold text-gray-900">
                          {ctx.commercial.contractValue.toLocaleString('pl-PL')} {ctx.commercial.currency || 'PLN'}
                        </span>
                      )}
                      {ctx.commercial?.paymentStatus && <Chip tone="gray">{ctx.commercial.paymentStatus}</Chip>}
                    </div>
                  )}
                  <div>
                    <p className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wide mb-1">W zakresie (opłacone – nie wyceniać ponownie)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(ctx.commercial?.inScope || []).length ? ctx.commercial.inScope.map((x, i) => <Chip key={i} tone="green">{x}</Chip>) : <Empty text="nie określono" />}
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide mb-1">Poza zakresem (legalny upsell)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(ctx.commercial?.outOfScope || []).length ? ctx.commercial.outOfScope.map((x, i) => <Chip key={i} tone="blue">{x}</Chip>) : <Empty text="nie określono" />}
                    </div>
                  </div>
                  {(ctx.commercial?.concessions || []).length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Ustępstwa / rabaty</p>
                      {ctx.commercial.concessions.map((c, i) => (
                        <p key={i} className="text-xs text-gray-700">• <b>{c.value}</b> {c.what} {c.reason ? <span className="text-gray-400">— {c.reason}</span> : null} <span className="text-gray-300">{fmtDate(c.date)}</span></p>
                      ))}
                    </div>
                  )}
                </div>
              </Section>

              {/* threads */}
              <Section title="Wątki" icon={<MessageCircle className="w-4 h-4 text-blue-600" />} count={(ctx.threads || []).length}>
                {(ctx.threads || []).length ? (
                  <div className="space-y-2">
                    {ctx.threads.map((t, i) => (
                      <div key={i} className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${t.status === 'resolved' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                          <p className="text-sm font-medium text-gray-800">{t.title}</p>
                          <span className="ml-auto text-[11px] text-gray-400">{fmtDate(t.lastTouched)}</span>
                        </div>
                        {t.summary && <p className="text-xs text-gray-600 mt-1">{t.summary}</p>}
                        {(t.decided || []).length > 0 && (
                          <ul className="mt-1.5 space-y-0.5">
                            {t.decided.map((d, j) => <li key={j} className="text-[11px] text-emerald-700 flex gap-1"><CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />{d}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : <Empty text="brak wątków" />}
              </Section>

              {/* voice */}
              {(ctx.voice?.perContact?.length || ctx.voice?.globalDonts?.length || ctx.voice?.sample) ? (
                <Section title="Profil głosu" icon={<Quote className="w-4 h-4 text-purple-600" />}>
                  {(ctx.voice?.perContact || []).map((v, i) => (
                    <div key={i} className="mb-2">
                      <p className="text-xs font-medium text-gray-800">{v.contact || 'domyślny'} <span className="text-gray-400 font-normal">— {v.tone} {v.formality ? `· ${v.formality}` : ''}</span></p>
                      {(v.donts || []).length > 0 && <p className="text-[11px] text-rose-600 mt-0.5">nie: {v.donts.join(', ')}</p>}
                    </div>
                  ))}
                  {(ctx.voice?.globalDonts || []).length > 0 && (
                    <p className="text-[11px] text-rose-600 mt-1">globalnie nie: {ctx.voice.globalDonts.join(', ')}</p>
                  )}
                  {ctx.voice?.sample && <p className="text-xs text-gray-600 mt-2 italic border-l-2 border-purple-200 pl-2">„{ctx.voice.sample}”</p>}
                </Section>
              ) : null}

              {/* decisions */}
              {(ctx.decisions || []).length > 0 && (
                <Section title="Log decyzji" icon={<FileText className="w-4 h-4 text-gray-600" />} count={ctx.decisions.length}>
                  {ctx.decisions.map((d, i) => (
                    <div key={i} className="text-xs text-gray-700 mb-1.5">
                      <span className="text-gray-400">{fmtDate(d.at)} · </span><b>{d.decision}</b>
                      {d.rationale && <span className="text-gray-500"> — {d.rationale}</span>}
                    </div>
                  ))}
                </Section>
              )}

              {/* event log */}
              <Section title="Log zdarzeń" icon={<CircleDot className="w-4 h-4 text-gray-600" />} count={events.length}>
                {events.length ? (
                  <div className="space-y-2">
                    {events.map((e) => (
                      <div key={e._id} className="flex gap-2.5 text-xs">
                        <span className={`mt-0.5 shrink-0 ${e.direction === 'in' ? 'text-amber-500' : e.direction === 'out' ? 'text-blue-500' : 'text-gray-400'}`}>
                          {channelIcon(e.channel)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-800">
                            <span className="font-medium">{e.actor || e.channel}</span>
                            {e.summary ? <span className="text-gray-600"> — {e.summary}</span> : null}
                          </p>
                          {e.significance && <p className="text-[11px] text-gray-400">{e.significance}</p>}
                        </div>
                        <span className="text-[11px] text-gray-400 shrink-0">{fmtDateTime(e.at)}</span>
                      </div>
                    ))}
                  </div>
                ) : <Empty text="brak zdarzeń" />}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
